import React, { useEffect, useState } from 'react';
import classnames from 'classnames/bind';
import { Fade } from 'react-awesome-reveal';
import { Logo } from '../../components/Logo';
import { Button } from '../../components/Button';
import { ProgressiveImage } from '../../components/ProgressiveImage';
import { RoleSelectorStage } from '../../components/RoleSelectorStage';
import { StarCardSelectorStage } from '../../components/StarCardSelectorStage';
import { logEvent } from '../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../lib/shared/event-types';
import { preloadImages } from '../../lib/shared/preload-images';
import cardImage1Src from '../../assets/images/prelander/card-v3-1.png';
import cardImage2Src from '../../assets/images/prelander/card-v3-2.png';
import cardImage3Src from '../../assets/images/prelander/card-v3-3.png';
import mainImageSrc from '../../assets/images/prelander/main-v3.jpg';
import rewardImageSrc from '../../assets/images/prelander/reward-v3.jpg';
import sRatingImageSrc from '../../assets/images/prelander/s-rating.png';
import aRatingImageSrc from '../../assets/images/prelander/a-rating.png';
import aPlusRatingImageSrc from '../../assets/images/prelander/a-plus-rating.png';
import { useExternalLink } from '../../shared/useExternalLink';
import styles from './PrelanderV3Page.css';

const cx = classnames.bind(styles);

const ROLE_SELECTOR_STAGE = 'roleSelectorStage';
const STAR_CARD_SELECTOR_STAGE = 'starCardSelectorStage';

const LEADER_ROLE = 'leader';
const SHOOTER_ROLE = 'shooter';
const DEFENDER_ROLE = 'defender';

const CARDS = [
    {
        imageUrl: cardImage1Src,
        id: 1,
        role: LEADER_ROLE,
        selectedRole: '',
        isStarCard: false
    },
    {
        imageUrl: cardImage2Src,
        id: 2,
        role: SHOOTER_ROLE,
        selectedRole: '',
        isStarCard: false
    },
    {
        imageUrl: cardImage3Src,
        id: 3,
        role: DEFENDER_ROLE,
        selectedRole: '',
        isStarCard: false
    }
];

const INITIAL_PRELANDER = 'v3';

const IMAGES = [
    ...CARDS.map(card => card.imageUrl),
    rewardImageSrc
];

function PrelanderV3Page({subscriptionPathname = 'subscription/v4', initialPrelander = INITIAL_PRELANDER}) {
    const [isPreview, setIsPreview] = useState(true);
    const [selectedCardsWithRole, setSelectedCardsWithRole] = useState(CARDS);
    const [stage, setStage] = useState('');
    const [isFinish, setIsFinish] = useState(false);

    useEffect(() => {
        logEvent(EVENT_TYPES.USER_PROPERTIES, {initialPrelander});
        logEvent(EVENT_TYPES.V3_PRELANDER_VISITED);

        if (isPreview) {
            preloadImages(IMAGES);
        }
    }, []);

    useEffect(() => {
        if (isFinish) {
            logEvent(EVENT_TYPES.V3_REWARD_SCREEN_VISITED);
        }
    }, [isFinish]);

    useEffect(() => {
        if (stage === ROLE_SELECTOR_STAGE) {
            logEvent(EVENT_TYPES.V3_PICK_CARDS_ROLE_SCREEN_VISITED);
        }

        if (stage === STAR_CARD_SELECTOR_STAGE) {
            logEvent(EVENT_TYPES.V3_PICK_STAR_CARD_SCREEN_VISITED);
        }
    }, [stage]);

    const selectRoleByCard = (role, cardId) => {
        setSelectedCardsWithRole(cards => cards.map(card => card.id === cardId ? {...card, selectedRole: role} : card));
    };

    const selectStarCard = cardId => {
        setSelectedCardsWithRole(cards => cards.map(card => ({...card, isStarCard: card.id === cardId})));
    };

    const startGame = () => {
        setIsPreview(false);
        setStage(ROLE_SELECTOR_STAGE);
    };

    const nextStarCardSelectorStage = () => {
        setStage(STAR_CARD_SELECTOR_STAGE);
    };

    const finishGame = () => {
        setIsFinish(true);
        setStage('');
    };

    const getResults = () => {
        const [firstCard, secondaryCard, tertiaryCard] = selectedCardsWithRole;
    
        if (firstCard.selectedRole === firstCard.role && secondaryCard.selectedRole === secondaryCard.role && tertiaryCard.selectedRole === tertiaryCard.role) {
            if (tertiaryCard.isStarCard) {
                return {
                    imageUrl: sRatingImageSrc,
                    description: 'flawless'
                }
            }

            return {
                imageUrl: aPlusRatingImageSrc,
                description: 'almost perfect'
            }
        }

        return {
            imageUrl: aRatingImageSrc,
            description: 'perfect'
        }
    };

    const renderContent = () => {
        if (isPreview) {
            return (
                <>
                    <main className={styles.main}>
                        <span className={styles.label}>For new users only</span>
                        <h1 className={styles.title}>
                            Free-to-play card fantasy game for counter-strike
                        </h1>
                        <p className={styles.smallDescription}>1-minute mini-game</p>
                        <ProgressiveImage
                            alt='counter-strike team'
                            src={mainImageSrc}
                            className={styles.image}
                        />
                        <p className={styles.description}>And get <strong>50 game cards ultimate subscription box</strong></p>
                    </main>
                    <Button
                        label='Continue'
                        className={styles.button}
                        onClick={startGame}
                        type='fifth'
                    />
                </>
            )
        };


        if (stage === ROLE_SELECTOR_STAGE) {
            return (
                <>
                    <main className={cx('main', 'mainWithIndent')}>
                        <span className={styles.label}>The challenge is</span>
                        <h1 className={styles.secondaryTitle}>
                            Which play role is best suited for each card?
                        </h1>
                        <RoleSelectorStage
                            cards={selectedCardsWithRole}
                            selectRoleByCard={selectRoleByCard}
                            finishStage={nextStarCardSelectorStage}
                        />
                    </main>
                    <Button
                        label='Continue'
                        className={cx('button', 'hiddenOnMobile')}
                        disabled={selectedCardsWithRole.some(card => !card.selectedRole)}
                        onClick={nextStarCardSelectorStage}
                        type='fifth'
                    />
                </>
            )
        };

        if (stage === STAR_CARD_SELECTOR_STAGE) {
            return (
                <>
                    <main className={cx('main', 'mainWithIndent')}>
                        <span className={styles.label}>One last step</span>
                        <h1 className={styles.secondaryTitle}>
                            Pick the “star” player who gets bonus points
                        </h1>
                        <StarCardSelectorStage
                            cards={selectedCardsWithRole}
                            selectStarCard={selectStarCard}
                        />
                    </main>
                    <Button
                        label='Continue'
                        className={styles.button}
                        onClick={finishGame}
                        disabled={!selectedCardsWithRole.some(card => card.isStarCard)}
                        type='fifth'
                    />
                </>
            )
        };

        if (isFinish) {
            const {description, imageUrl} = getResults();
            const {url: subscriptionUrl, onClick: clickOnClaimRewards} = useExternalLink(subscriptionPathname);

            return (
                <Fade
                    triggerOnce
                    duration={300}
                >
                    <main className={styles.main}>
                        <span className={styles.label}>congratulations!</span>
                        <h1 className={styles.title}>
                            Top reward unlocked
                        </h1>
                        <div className={styles.rewardBlock}>
                            <ProgressiveImage
                                alt='reward'
                                src={rewardImageSrc}
                                className={styles.rewardImage}
                            />
                            <ProgressiveImage
                                alt='rating'
                                src={imageUrl}
                                className={styles.ratingImage}
                            />
                            <p className={styles.ratingDescription}>{description}!</p>
                        </div>
                        <p className={styles.description}>50 game cards ultimate subscription box is already waiting for you on the platform.</p>
                    </main>
                    <Button
                        label='Claim all rewards'
                        className={styles.button}
                        onClick={clickOnClaimRewards}
                        link={{
                            to: subscriptionUrl,
                            target: 'custom'
                        }}
                        type='fifth'
                    />
                </Fade>
            )
        }

        return null;
    };

    const {url: mainUrl, onClick: clickOnLogin} = useExternalLink('counter-strike', 'auth=true');

    return (
        <div className={styles.container}>
            <div className={styles.headerWrapper}>
                <header className={styles.header}>
                    <div className={styles.headerMainInformation}>
                        <Logo className={styles.logo}/>
                        <p className={styles.headerTitle}>Free-to-play card fantasy game for counter-strike</p>
                    </div>
                    <Button
                        iconLeft={{type: 'login'}}
                        link={{
                            to: mainUrl
                        }}
                        onClick={clickOnLogin}
                        type='tertiary'
                    />
                </header>
            </div>
           {renderContent()}
            <footer className={styles.footer}>
                <p className={styles.copyrightText}>
                    Powerplay is developed independently and is not affiliated with or endorsed by the players, video game developers, esports teams or event holders depicted in the game.
                </p>
            </footer>
        </div>
    )
}

export {
    PrelanderV3Page
}
